import { Container, Card, Row } from 'react-bootstrap'
import { CardRowItem } from '../../cardRowItem'
import { ColoredBlock } from '@mmsg/louie'
import * as S from './proposalElements'
import { formatCurrency, useWindowSize } from 'helpers'
import { useSelector } from 'react-redux'
import { getCurrentResolutionBreakPoint } from 'utils/utilityFunctions'
import { BText } from 'components/styledComponents/text'
import styled from '@emotion/styled'
import FeesChargesWithoutTaxImplicationSection from './feesChargesWithoutTaxImplicationsSection'
import { IProposalDeductionSectionProps } from './proposalTypes'
import { ITheme } from 'utils/type'

interface IStatePDFProps {
  downloadPdf: { isDownloading: boolean }
}
interface IStateIsGovProps {
  individualEstimate: { isGov: string }
}

interface IInputProps {
  data: IProposalDeductionSectionProps
  userEmployer: { employerGRS?: { gstExclusiveDeductionsCode?: string } }
  downloadingPdf?: boolean
  isAnnual?: boolean
  pdf?: boolean
  title: string
  theme: ITheme
}

const DeductionSection = (props: IInputProps) => {
  const isDownloadingPdf = useSelector(
    (state: IStatePDFProps) => state.downloadPdf.isDownloading
  )
  const resolutionBreakpoint = {
    nonDesktop: getCurrentResolutionBreakPoint(isDownloadingPdf),
  }

  const isGov = useSelector(
    (state: IStateIsGovProps) => state.individualEstimate.isGov
  )

  const [width] = useWindowSize()

  let isExGST =
    props.userEmployer &&
    props.userEmployer.employerGRS &&
    props.userEmployer.employerGRS.gstExclusiveDeductionsCode &&
    props.userEmployer.employerGRS.gstExclusiveDeductionsCode.toLowerCase() ===
      'yes'
      ? true
      : false

  let data = {
    carRepayment:isExGST ? props.data.carRepaymentPerPayExGST : props.data.carRepaymentPerPayIncGST,
    fuel: isExGST ? props.data.fuelPerPayExGST : props.data.fuelPerPayIncGST,
    electricity: isExGST ? props.data.electricityPerPayExGST : props.data.electricityPerPayIncGST,
    maintenance: isExGST ? props.data.maintenancePerPayExGST : props.data.maintenancePerPayIncGST,
    tyres: isExGST ? props.data.tyresPerPayExGST : props.data.tyresPerPayIncGST,
    incidentals: isExGST ? props.data.incidentalsPerPayExGST : props.data.incidentalsPerPayIncGST,
    roadside: isExGST ? props.data.roadsidePerPayExGST : props.data.roadsidePerPayIncGST,
    rego: isExGST ? props.data.regoPerPayExGST : props.data.regoPerPayIncGST,    
    compIns: isExGST ? props.data.compInsPerPayExGST : props.data.compInsPerPayIncGST,
    nlFee: isExGST ? props.data.nlFeePerPayExGST : props.data.nlFeePerPayIncGST,
    totalPackage: isExGST ? props.data.totalPackagePerPayExGST : props.data.totalPackagePerPayIncGST,
    spFee: isExGST ? props.data.spFeePerPayExGST : props.data.spFeePerPayIncGST,
    fbt: props.data.fbtPerPay,
    lcda: props.data.lcdaPerPay,
    greenfleet: props.data.greenfleetPerPay,
    gstOnECM: props.data.gstOnECMPerPay,
    erShare: props.data.erSharePerPay,
    erAdminFee: props.data.erAdminFeePerPay,
  }
  
  let shouldTaxImplicationBeVisible = fnCheckIfTaxImplocationBeVisible()

  //Variable FINANCED PAYMENTS subtext
  const input = [`Includes the car on roads`]

  let insuranceText = "Comprehensive insurance renewal" 
  if(props.data.compInsuranceProvider === 'Allianz')
    insuranceText += ' (Oly arranged)'

  if (props.data && props.data.financierEstablishmentFee)
    input.push(`Financier's establishment Fee`)
  if (
    props.data.leaseProtectionPremium +
      props.data.rpcPremium +
      props.data.extendedWarrantyPremium +
      props.data.gapCoverPremium >
    0
  )
    input.push('add-on lease insurances')
  const last = input.pop() //If nothing else pushed on, this will be the 'Includes the car on roads', and input will be empty.
  let financedPaymentsTxt =
    input.join(', ') + (input.length > 0 ? ' and ' : '') + last + '.'

  // Function to check if the tax implication section should be displayed or not
  function fnCheckIfTaxImplocationBeVisible() {
    let shouldBeVisible = false

    if ((data.fbt && data.fbt > 0) ||
        (data.lcda && data.lcda > 0) ||
        (data.greenfleet && data.greenfleet > 0) ||
        (isExGST &&
          props.data &&
          props.data.fbtMethod &&
          props.data.fbtMethod.toLowerCase() === 'ecm' &&
          data.gstOnECM &&
          data.gstOnECM > 0) ||
        (props.data &&
          props.data.taxShare &&
          props.data.taxShare.toLowerCase() !== 'no' &&
          data.erShare &&
          data.erShare > 0) ||
        (data.erAdminFee && data.erAdminFee > 0)
    ) {
      shouldBeVisible = true
    }

    return shouldBeVisible
  }

  const displayGSTonECM = (): boolean => {
    if (!isExGST) return false

    if (
      props.data.fbtMethod &&
      props.data.fbtMethod.toLowerCase().indexOf('ecm') > -1
    )
      return true
    if (
      props.data.overTheCapMethod &&
      props.data.overTheCapMethod.toLowerCase().indexOf('ecm') > -1
    )
      return true
    return false
  }

  const taxImplication = () => (
    <>
      {shouldTaxImplicationBeVisible && (
        <div>
          <div className="font-weight-bold text-uppercase">
            Tax Implications & Other Charges
          </div>
          <CardRowItem
            title="FBT"
            value={data.fbt}
            isCurrency="true"
            hideIfNoValue="true"
          />

          <CardRowItem
            title="Luxury car depreciation allowance"
            value={data.lcda}
            isCurrency="true"
            hideIfNoValue="true"
          />
          <CardRowItem
            title="Greenfleet donation"           
            value={data.greenfleet}
            isCurrency="true"
            hideIfNoValue="true"
          />
          {displayGSTonECM() && (
            <CardRowItem
              title="GST on ECM"
              value={data.gstOnECM}
              isCurrency="true"
              hideIfNoValue="true"
            />
          )}
          {props.data.taxShare.toLowerCase() !== 'no' && (
            <CardRowItem
              title="Employer share of benefit"
              value={data.erShare}
              isCurrency="true"
              hideIfNoValue="true"
            />
          )}
          <CardRowItem
            title="Tax share"
            value={data.erAdminFee}
            isCurrency="true"
            hideIfNoValue="true"
          />
        </div>
      )}
    </>
  )

  const NlDeductions = () => {
    let isMobile = width < resolutionBreakpoint.nonDesktop
    if (isMobile)
      return (
        <S.DeductionTotalContainerMobile theme={props.theme}>
          <S.DeductionColorBlockMobile theme={props.theme}>
            <h2 className="mb-2 text-medium">Estimated out of pocket</h2>
            <b>{formatCurrency(props.data.outOfPocketPerPay)}</b>
          </S.DeductionColorBlockMobile>
          <div className="text-center mt-2">
            <div>Total Salary Packaging Deductions</div>
            <b>{formatCurrency(data.totalPackage)}</b>
          </div>
          <div className="m-2" style={{ fontSize: '12px' }}>
            Less estimated Tax and Medicare Levy Savings
          </div>
        </S.DeductionTotalContainerMobile>
      )
    else
      return (
        <S.DeductionTotalContainer>
          <div>
            <S.DeductionTotalBlock theme={props.theme}>
              <div>Total Novated Lease Deductions</div>
              <b>{formatCurrency(data.totalPackage)}</b>
            </S.DeductionTotalBlock>
            <div className="text-small mb-2">
              Less estimated Tax and Medicare Levy Savings
            </div>
          </div>
          <ColoredBlock brand="Oly">
            {' '}
            <h2 className="mt-2 mb-2 text-small">Estimated out of pocket</h2>
            <b className="text-largest">{formatCurrency(props.data.outOfPocketPerPay)}</b>
          </ColoredBlock>
        </S.DeductionTotalContainer>
      )
  }

  return (
    <div className="card-section print-page-break-before" id="deductionsection">
      <Container className="section mt-4">
        <Card
          className={
            props.pdf ? 'mb-13rem pdf-card-container' : 'mb-13rem card-shadow'
          }
        >
          <Card.Header
            className={
              props.pdf ? 'border-bottom-0 pdf-card' : 'border-bottom-0'
            }
          >
            <div className="text-header">{props.title}</div>
          </Card.Header>
          <Card.Body>
            <div className="font-weight-bold text-uppercase">
              Financed Payments
            </div>
            <CardRowItem
              title={financedPaymentsTxt}
              value={data.carRepayment}
              isCurrency="true"
              hideIfNoValue="true"
              normalWeight
            />
            
            <hr></hr>
            <div className="font-weight-bold text-uppercase">
              Running Costs & Servicing
            </div>
            <CardRowItem
              title="Fuel"
              value={data.fuel}
              isCurrency="true"
              hideIfNoValue="true"
            />
            <CardRowItem
              title="Electricity"
              value={data.electricity}
              isCurrency="true"
              hideIfNoValue="true"
            />
            <CardRowItem
              title="Scheduled servicing"
              value={data.maintenance}
              isCurrency="true"
              hideIfNoValue="true"
            />
            <CardRowItem
              title="Replacement tyres"
              value={data.tyres}
              isCurrency="true"
              hideIfNoValue="true"
            />
            <CardRowItem
              title="Incidentals"
              value={data.incidentals}
              isCurrency="true"
              hideIfNoValue="true"
            />
            <CardRowItem
              title="Roadside assistance"
              value={data.roadside}
              isCurrency="true"
              hideIfNoValue="true"
            />
            <hr />
            <div className="font-weight-bold text-uppercase">
              Registration & Insurance
            </div>
            <CardRowItem
              title="Registration renewal"
              value={data.rego}
              isCurrency="true"
              hideIfNoValue="true"
            />
            {data.compIns > 0 &&
              <CardRowItem
                title={insuranceText}
                value={data.compIns}
                isCurrency="true"
                hideIfNoValue="true"
              />
            }
            <hr></hr>

            <div>
              {(data.nlFee > 0 || data.spFee > 0) && (
                <>
                  <div className="font-weight-bold text-uppercase">
                    Novated Lease Fees & Charges
                  </div>
                  <CardRowItem
                    title="Oly novated leasing fee"
                    value={data.nlFee}
                    isCurrency="true"
                    hideIfNoValue="true"
                  />
                  <CardRowItem
                    title="Salary packaging fee"
                    value={data.spFee}
                    isCurrency="true"
                    hideIfNoValue="true"
                  />
                </>
              )}
              {props.downloadingPdf && (
                <div>
                  <hr></hr>
                  <ToBeContinuedBlock>
                    <BText>Continued on next page</BText>
                  </ToBeContinuedBlock>
                </div>
              )}
              {!props.downloadingPdf && (
                <>
                  <hr></hr>
                  {taxImplication()}
                </>
              )}
            </div>
          
          </Card.Body>
          {!props.downloadingPdf && <NlDeductions />}
        </Card>
      </Container>
      {props.downloadingPdf && (
        <Container className="section mt-4">
          <Card
            className={props.pdf ? 'mb-13rem pdf-card-container' : 'mb-13rem'}
          >
            <Card.Header
              className={
                props.pdf ? 'border-bottom-0 pdf-card' : 'border-bottom-0'
              }
            >
              <div className="text-header">{props.title}</div>
            </Card.Header>
            <Card.Body>{taxImplication()}</Card.Body>
            
            <NlDeductions />
          </Card>
        </Container>
      )}
    </div>
  )
}

export default DeductionSection

const ToBeContinuedBlock = styled('div')`
  label: ToBeContinuedBlock;
  display: flex;
  flex-direction: row-reverse;
`
